import React, { useEffect, useState } from 'react';
import client from '../client/Client';

interface Organizer {
  image: {
    asset: {
      _ref: string;
    };
  };
  ORGName: string;
  url?: string;
}



interface OrganizersDoc {
  Organizers: Organizer[];
}



// ---- CONSTANTS ----
const ITEM_WIDTH = 200;        // width in px for each logo "slot"
const SPEED = 100;              // pixels per second (same for all bands)

// ---- HELPERS ----
const getSanityImageUrl = (ref?: string): string => {
  if (!ref) return '';
  const id = ref.slice(6, -4);
  const ext = ref.slice(-3);
  return `https://cdn.sanity.io/images/79vgbaej/production/${id}.${ext}`;
};

const LogoBox: React.FC<{ src?: string; alt?: string; url?: string }> = ({
  src,
  alt,
  url,
}) => {
  const image = src ? (
    <img
      className="object-contain h-full w-full"
      src={src}
      alt={alt || 'logo'}
    />
  ) : (
    <div className="w-full h-full bg-gray-300 flex items-center justify-center">
      <span className="text-gray-500 text-sm">No Logo</span>
    </div>
  );

  return (
    <div className="h-24 w-40 mx-4">
      {url ? (
        <a href={url} target="_blank" rel="noopener noreferrer">
          {image}
        </a>
      ) : (
        image
      )}
    </div>
  );
};

const MovingBand: React.FC = () => {
  const [organizers, setOrganizers] = useState<Organizer[]>([]);

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const organizersQuery = client.fetch<OrganizersDoc[]>('*[_type == "Organizers"]');

    Promise.all([organizersQuery])
      .then(([orgResult]) => {
        // Organizers
        if (orgResult?.length && orgResult[0].Organizers) {
          setOrganizers(orgResult[0].Organizers);
        }

      })
      .catch((error) => {
        console.error('Error fetching organizers/sponsors data:', error);
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="relative bg-gray-100 py-8">
        <div className="text-center mb-6">
          <h2 className="text-2xl font-bold text-red-600">
            Loading organizers ...
          </h2>
        </div>
        <div className="h-32 flex items-center justify-center">
          <div className="text-gray-500">Please wait</div>
        </div>
      </div>
    );
  }

  // Utility to build duplicated list + animation metrics
  const buildBand = <T extends { image?: { asset?: { _ref?: string } }; name?: string; ORGName?: string; url?: string }>(
    items: T[]
  ) => {
    const baseCount = items.length;
    const baseLengthPx = baseCount * ITEM_WIDTH;
    const distancePx = baseLengthPx * 2;        // how far we scroll before looping
    const trackWidthPx = baseLengthPx * 4;      // 4x duplication, very safe
    const durationSec = distancePx > 0 ? distancePx / SPEED : 40; // fallback

    const duplicated = [...items, ...items, ...items, ...items];

    return {
      duplicated,
      trackWidthPx,
      distancePx,
      durationSec,
    };
  };

  const orgBand = buildBand(organizers);


  return (
    <div className="relative bg-gray-100 py-8 space-y-12">
      {/* === ORGANIZERS BAND === */}
      <section>
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold text-blue-901 mb-4">Organizers</h2>
        </div>

        {organizers.length === 0 ? (
          <div className="h-32 flex items-center justify-center">
            <div className="text-gray-500">No organizers found</div>
          </div>
        ) : (
          <div className="relative h-40 overflow-hidden">
            <div
              className="absolute inset-0 flex items-center"
              style={{
                width: `${orgBand.trackWidthPx}px`,
                animation: `scrollOrg ${orgBand.durationSec}s linear infinite`,
              }}
            >
              {orgBand.duplicated.map((item, index) => (
                <div
                  key={`${item.ORGName || 'org'}-${index}`}
                  className="flex-shrink-0 h-full flex items-center justify-center"
                  style={{ width: `${ITEM_WIDTH}px` }}
                >
                  <LogoBox
                    src={getSanityImageUrl(item.image?.asset?._ref)}
                    alt={item.ORGName}
                    url={item.url}
                  />
                </div>
              ))}
            </div>  

            <div className="absolute left-0 top-0 h-full w-8 bg-gradient-to-r from-gray-100 to-transparent z-10" />
            <div className="absolute right-0 top-0 h-full w-8 bg-gradient-to-l from-gray-100 to-transparent z-10" />
          </div>
        )}
      </section>

      

      

      {/* Per-band keyframes, with distances baked in */}
      <style>{`
        @keyframes scrollOrg {
          0% {
            transform: translateX(0);
          }
          100% {
            transform: translateX(-${orgBand.distancePx}px);
          }
        }

        }
      `}</style>
    </div>
  );
};

export default MovingBand;
